import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import type { PortalPromptSelectOption, PortalPromptSelectOptions } from '../../../services/portal-dialog.types';

@Component({
  selector: 'app-portal-prompt-select-dialog',
  templateUrl: './portal-prompt-select-dialog.component.html',
  styleUrls: ['./portal-prompt-select-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PortalPromptSelectDialogComponent implements OnDestroy {
  private _options: PortalPromptSelectOptions = { title: '', options: [] };

  @Input()
  set options(value: PortalPromptSelectOptions) {
    this._options = value || { title: '', options: [] };
    this.applyOptions(this._options);
  }
  get options(): PortalPromptSelectOptions {
    return this._options;
  }

  busy = false;
  searching = false;
  title = '';
  message = '';
  confirmLabel = '';
  cancelLabel = '';
  confirmVariant: 'primary' | 'danger' = 'primary';
  size: 'sm' | 'md' | 'lg' = 'sm';
  inputLabel = '';
  value = '';
  error = '';
  searchable = false;
  searchPlaceholder = '';
  query = '';
  toggleLabel = '';
  showExtra = false;
  selectOptions: PortalPromptSelectOption[] = [];
  filteredOptions: PortalPromptSelectOption[] = [];

  private searchTimer: ReturnType<typeof setTimeout> | null = null;
  private searchSeq = 0;

  constructor(
    public activeModal: NgbActiveModal,
    private cdr: ChangeDetectorRef,
  ) {}

  private applyOptions(raw: PortalPromptSelectOptions): void {
    const o = raw || { title: '', options: [] };
    this.title = o.title || '';
    this.message = o.message || '';
    this.confirmLabel = o.confirmLabel || '';
    this.cancelLabel = o.cancelLabel || '';
    this.confirmVariant = o.confirmVariant === 'danger' ? 'danger' : 'primary';
    this.size = o.size || 'sm';
    this.inputLabel = o.inputLabel || '';
    this.selectOptions = Array.isArray(o.options) ? o.options : [];
    this.searchPlaceholder = o.searchPlaceholder || '';
    this.toggleLabel = o.toggle?.label || '';
    this.showExtra = !!o.toggle?.initial;
    this.query = '';
    this.value = o.initialValue != null ? String(o.initialValue) : '';
    this.refreshDisplayedOptions();
    if (o.initialValue == null && !this.searchable) {
      this.value = this.filteredOptions[0]?.value ?? '';
    }
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
  }

  dismiss(): void {
    if (this.busy) return;
    this.activeModal.dismiss('cancel');
  }

  onValueChange(next: string): void {
    this.value = next;
    if (this.error) {
      this.error = '';
    }
    this.cdr.markForCheck();
  }

  onToggle(next: boolean): void {
    this.showExtra = next;
    if (!next && this.selectOptions.some((opt) => opt.value === this.value && opt.hiddenUntilToggle)) {
      this.value = '';
    }
    this.refreshDisplayedOptions();
    this.cdr.markForCheck();
  }

  onQueryChange(next: string): void {
    this.query = next;
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
    if (!this.options?.searchFn) {
      this.applyLocalFilter();
      this.cdr.markForCheck();
      return;
    }
    this.searchTimer = setTimeout(() => {
      void this.runRemoteSearch(this.query);
    }, 280);
  }

  pickOption(value: string): void {
    this.onValueChange(value);
  }

  submit(): void {
    if (this.busy) return;
    const validator = this.options?.validator;
    if (validator) {
      const msg = validator(this.value);
      if (msg) {
        this.error = msg;
        this.cdr.markForCheck();
        return;
      }
    }
    this.activeModal.close(this.value);
  }

  private visibleOptions(): PortalPromptSelectOption[] {
    if (this.showExtra || !this.toggleLabel) {
      return this.selectOptions;
    }
    return this.selectOptions.filter((opt) => !opt.hiddenUntilToggle);
  }

  private refreshDisplayedOptions(): void {
    const visible = this.visibleOptions();
    this.searchable =
      this.options?.searchable === true || !!this.options?.searchFn || visible.length > 12;
    this.applyLocalFilter();
  }

  private applyLocalFilter(): void {
    const visible = this.visibleOptions();
    const needle = this.query.trim().toLowerCase();
    if (!needle) {
      this.filteredOptions = visible;
      return;
    }
    this.filteredOptions = visible.filter((opt) => {
      const hay = `${opt.label} ${opt.value}`.toLowerCase();
      return hay.includes(needle);
    });
  }

  private async runRemoteSearch(query: string): Promise<void> {
    const searchFn = this.options?.searchFn;
    if (!searchFn) {
      return;
    }
    const seq = ++this.searchSeq;
    this.searching = true;
    this.cdr.markForCheck();
    try {
      const rows = await searchFn(query.trim());
      if (seq !== this.searchSeq) {
        return;
      }
      this.filteredOptions = Array.isArray(rows) ? rows : [];
    } catch {
      if (seq !== this.searchSeq) {
        return;
      }
      this.filteredOptions = [];
    } finally {
      if (seq === this.searchSeq) {
        this.searching = false;
        this.cdr.markForCheck();
      }
    }
  }
}
